import { ProjectType, ProjectStatus } from 'types/data/projects/constants';
import { Title, NarrowContainer } from 'components/Projects/CreateAndImport';
import { InputType, SetState } from 'types/index';
import {
	CreateProject,
	Project,
	ProjectFormValues,
	ProjectMetadata,
	ProjectMetadataType
} from 'store/data/projects';
import { Column, Row } from './CreateProjectForm.style';
import { Input } from 'components/UI/Inputs/Input';
import { Spacer } from 'components/UI/Spacer';
import { RequiredFields } from 'components/UI/RequiredFields';
import { StickyFooter } from 'components/UI/StickyFooter';
import { useNavigation } from 'hooks/navigation';
import { useProjectMetadataDefinition, useTranslation } from 'hooks/store';
import { useCompletedAction, useEffectOnce, useDispatch } from 'hooks/utils';
import { getProjects, setProjectId } from 'store/data/projects';
import {
	getInitialMetadataFormValues,
	parseFormValuesToProject,
	transforMetadataParametersToFormRows,
	validateProjectForm
} from 'helpers/projects/projectMetadata';
import { InfoTooltip } from 'components/UI/Interactables/InfoTooltip';
import { CreatableSelect } from 'components/UI/Interactables/CreatableSelect';
import { parseFromApiMetadata } from 'store/data/projects/parsers';
import { useCallback } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Form } from 'hooks/ui/useReactForm';
import { useState } from 'react';
import { InfoMessage } from 'components/UI/InfoMessage';
import { useIsCodelistFeatureEnabled } from 'features/codelist/useIsCodeListEnabled';
import { CodelistRadioGroup } from 'components/Projects/CodelistRadioGroup';
import { AdminApprovalSuccess } from '../AdminApprovalSuccess/AdminApprovalSuccess';
import { useTranslatedMetadataLabel } from 'hooks/useTranslatedMetadataLabel';

interface Props {
	draftProject: CreateProject | null;
	setDraftProject: SetState<CreateProject | null>;
	createProjectController: {
		loading: boolean;
		error: boolean;
		create: (project: CreateProject) => void;
		data: Project | undefined;
	};
	onFinish: () => void;
}

export function CreateProjectForm({ draftProject, createProjectController, onFinish }: Props) {
	const { routes, navigate } = useNavigation();
	const dispatch = useDispatch();

	const { translate } = useTranslation();
	const [showAdminApproval, setShowAdminApproval] = useState(false);

	const [endDateError, setEndDateError] = useState<string>('');

	const getTranslatedMetadataLabel = useTranslatedMetadataLabel();
	const [{ data: projectMetadataDefinition }] = useProjectMetadataDefinition();
	const isCodelistFeatureEnabled = useIsCodelistFeatureEnabled();

	const {
		loading: creatingProject,
		error: errorCreatingProject,
		create: createProject,
		data: createdProject
	} = createProjectController;

	useCompletedAction(creatingProject, errorCreatingProject, () => {
		// Refresh the projects list to include the newly created project
		dispatch(getProjects());

		if (createdProject?.status === ProjectStatus.PendingAdminApproval) {
			setShowAdminApproval(true);
		} else {
			// Set the project ID in the Redux store so the app knows which project is active
			if (createdProject?.projectId) {
				dispatch(setProjectId({ projectId: createdProject.projectId }));
			}
			onFinish();
		}
	});

	// APPLY DRAFT PROJECT (IF EXISTS)
	useEffectOnce(() => {
		if (draftProject) {
			const { metadata, ...draftProjectValues } = draftProject;
			Object.keys(draftProjectValues).forEach(key => {
				setValue(
					key as keyof Omit<CreateProject, 'metadata'>,
					draftProjectValues[key as keyof Omit<CreateProject, 'metadata'>] ?? '',
					{
						shouldDirty: true,
						shouldValidate: true
					}
				);
			});

			const parsedMetadata = parseFromApiMetadata(metadata);

			if (parsedMetadata)
				Object.keys(parsedMetadata).forEach(key =>
					setValue(
						key as keyof ProjectMetadata,
						parsedMetadata[key as keyof ProjectMetadata],
						{
							shouldDirty: true,
							shouldValidate: true
						}
					)
				);
		}
	});

	const initialMetadataValues = getInitialMetadataFormValues(projectMetadataDefinition);

	const initialValues: ProjectFormValues = {
		projectName: '',
		givenProjectNumber: '',
		status: ProjectStatus.Ongoing,
		projectType: ProjectType.CORE,
		projectEndDate: '',
		description: '',
		slideFolderURL: '',
		...initialMetadataValues
	};

	const {
		register,
		setValue,
		getValues,
		handleSubmit,
		formState: { isDirty, isValid, isSubmitted },
		setError,
		control,
		watch
	} = useForm({
		defaultValues: initialValues,
		reValidateMode: 'onChange',
		mode: 'onChange',
		criteriaMode: 'all'
	});

	const { errors: selfManagedErrors, requiredFields } = validateProjectForm(
		projectMetadataDefinition,
		watch,
		translate,
		isCodelistFeatureEnabled
	);

	const handleTrimOnBlur = useCallback(
		(e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			const { name, value } = e.target;

			setValue(name as any, value.trim() as any);
		},
		[]
	);

	const schema = projectMetadataDefinition.metadataSchema;

	const handleFormSubmit = handleSubmit(() => {
		const canSubmitForm = isDirty && isValid && Object.keys(selfManagedErrors).length === 0;
		if (!canSubmitForm) {
			if (selfManagedErrors.codelistEnabled) {
				setError('codelistEnabled', {
					type: 'required',
					message: translate(dict => dict.projectMetadata.general.mandatoryField)
				});
			}

			return;
		}

		if (isDirty && !creatingProject) {
			trimFields();

			const values = { ...getValues() };

			const parsedValues = { ...values };

			if (
				!schema?.if?.properties.projectType.enum.includes(
					String(values['metadata_projectType'])
				)
			) {
				parsedValues['metadata_archiveNumber'] =
					values['metadata_archiveNumber']?.toString() ?? '';
			}

			createProject(parseFormValuesToProject(parsedValues, projectMetadataDefinition));
		}
	});

	function trimFields() {
		const values = { ...getValues() };

		setValue('projectName', values.projectName.toString().trim());
		setValue('description', values.description.toString().trim());
		setValue('givenProjectNumber', values.givenProjectNumber.toString().trim());

		projectMetadataDefinition.metadataDefinition &&
			projectMetadataDefinition.metadataDefinition.forEach(metadataParameter => {
				if (values[metadataParameter.name])
					setValue(
						`${metadataParameter.name}`,
						values[metadataParameter.name].toString().trim()
					);
			});
	}

	function checkProjectEndDate(dateStr: string) {
		if (dateStr === '' && endDateError !== '') {
			setEndDateError('');
			return;
		}
		const date = new Date(dateStr);

		date.setHours(0, 0, 0, 0);

		const now = new Date(new Date().setHours(0, 0, 0, 0));

		if (date < now) {
			setEndDateError(translate(dict => dict.createProject.projectEndDateError));
		} else {
			if (endDateError.length > 0) {
				setEndDateError('');
			}
		}
	}

	const formMetadataParameters = transforMetadataParametersToFormRows(projectMetadataDefinition);

	if (showAdminApproval) {
		return <AdminApprovalSuccess />;
	}

	return (
		<>
			<NarrowContainer>
				<Form onSubmit={handleFormSubmit}>
					<Title>
						{translate(({ projects }) => projects.createAndImport.title.create)}
					</Title>

					<Input
						{...register('projectName', {
							onBlur: handleTrimOnBlur
						})}
						type={InputType.Text}
						label={translate(dict => dict.createProject.projectTitle)}
						error={
							isSubmitted ? (selfManagedErrors.projectName?.message as string) : ''
						}
						dataTestId="project-name-input"
						required
						autoFocus
					/>
					<Spacer size={s => s.s} />
					<Input
						{...register('description', {
							onBlur: handleTrimOnBlur
						})}
						type={InputType.Textarea}
						label={translate(dict => dict.createProject.projectDescription)}
						dataTestId="project-description-input"
						rows={4}
					/>

					<Spacer size={s => s.s} />
					<Row>
						<Column>
							<Input
								{...register('givenProjectNumber', {
									onBlur: handleTrimOnBlur
								})}
								type={InputType.Text}
								label={translate(dict => dict.createProject.projectNumber)}
							/>
						</Column>
						<Column>
							<Controller
								control={control}
								name="projectEndDate"
								render={({ field: { value } }) => (
									<Input
										type={InputType.Date}
										value={value as number}
										label={translate(dict => dict.createProject.projectEndDate)}
										error={endDateError}
										onDateChange={({ formattedDate }) => {
											checkProjectEndDate(formattedDate);
											setValue('projectEndDate', formattedDate, {
												shouldDirty: true,
												shouldValidate: true
											});
										}}
									/>
								)}
							/>
						</Column>
					</Row>

					{formMetadataParameters.length > 0 && (
						<>
							<Spacer size={s => s.s} />
							{formMetadataParameters.map((metadataParametersRow, i) => {
								return (
									<div key={i}>
										<Spacer size={s => s.s} />
										<Row>
											{metadataParametersRow.map(metadataParameter => (
												<Column key={metadataParameter.name}>
													{metadataParameter.type ===
														ProjectMetadataType.String && (
														<Input
															{...register(
																`${metadataParameter.name}`,
																{
																	onBlur: handleTrimOnBlur
																}
															)}
															placeholder={translate(
																() => metadataParameter.placeholder
															)}
															type={InputType.Text}
															label={translate(
																() => metadataParameter.label
															)}
															error={
																isSubmitted
																	? (selfManagedErrors[
																			metadataParameter.name
																	  ]?.message as string)
																	: undefined
															}
															tooltipComponent={
																<InfoTooltip
																	marginOffset={{
																		left: 0.4,
																		bottom: 0.4
																	}}
																	iconVisible
																	text={translate(
																		() =>
																			metadataParameter.description
																	)}
																/>
															}
															required={requiredFields.has(
																metadataParameter.name
															)}
														/>
													)}

													{metadataParameter.type ===
														ProjectMetadataType.Dropdown && (
														<Controller
															name={`${metadataParameter.name}`}
															control={control}
															render={({
																field: { value, onBlur }
															}) => (
																<CreatableSelect
																	dataTestId={`${metadataParameter.name}-select`}
																	label={translate(
																		() =>
																			metadataParameter.label
																	)}
																	tooltipPlace="top"
																	tooltipComponent={
																		<InfoTooltip
																			marginOffset={{
																				left: 0.4,
																				bottom: 0.4
																			}}
																			iconVisible
																			text={translate(
																				() =>
																					metadataParameter.description
																			)}
																		/>
																	}
																	placeholder={translate(
																		() =>
																			metadataParameter.placeholder
																	)}
																	value={
																		value
																			? {
																					value: value.toString(),
																					label: getTranslatedMetadataLabel(
																						value as string
																					)
																			  }
																			: undefined
																	}
																	items={metadataParameter.categories
																		.filter(i => i.active)
																		.map(option => ({
																			label: translate(
																				() => option.name
																			),
																			value: `${option.id}`
																		}))}
																	error={
																		isSubmitted
																			? (selfManagedErrors[
																					metadataParameter
																						.name
																			  ]?.message as string)
																			: undefined
																	}
																	onClear={() => {
																		setValue(
																			`${metadataParameter.name}`,
																			'',
																			{
																				shouldDirty: true
																			}
																		);
																	}}
																	borderError={
																		isSubmitted &&
																		!!selfManagedErrors[
																			metadataParameter.name
																		]?.message
																	}
																	required={requiredFields.has(
																		metadataParameter.name
																	)}
																	onValueSelected={selectedValue =>
																		selectedValue &&
																		setValue(
																			`${metadataParameter.name}`,
																			selectedValue,
																			{
																				shouldDirty: true
																			}
																		)
																	}
																	onBlur={onBlur}
																	hint={translate(
																		() =>
																			metadataParameter.categories.find(
																				p => p.id === value
																			)?.description ?? ''
																	)}
																	scrollIntoView
																/>
															)}
														/>
													)}
												</Column>
											))}
										</Row>
									</div>
								);
							})}

							<Spacer size={s => s.s} />

							<InfoMessage
								message={
									<div>
										<p>{translate(dict => dict.projectMetadata.hso.info)}</p>
										<p>
											{translate(
												dict => dict.projectMetadata.hso.byProceeding
											)}
										</p>
									</div>
								}
							/>
						</>
					)}

					{isCodelistFeatureEnabled && (
						<>
							<Spacer size={s => s.l} />

							<CodelistRadioGroup
								register={register}
								fieldName="codelistEnabled"
								error={
									isSubmitted
										? selfManagedErrors.codelistEnabled?.message
										: undefined
								}
							/>

							<Spacer size={s => s.l} />
						</>
					)}

					<Spacer size={s => s.m} />
					<RequiredFields />
				</Form>
			</NarrowContainer>

			<StickyFooter
				primary={{
					label: translate(({ buttons }) => buttons.continue),
					loading: creatingProject,
					onClick: handleFormSubmit
				}}
				neutral={{
					label: translate(({ buttons }) => buttons.cancel),
					onClick: creatingProject ? undefined : () => navigate(routes.projects.list)
				}}
				maxWidth={65.2}
			/>
		</>
	);
}
